import type { ReactNode } from 'react';
import type { Feature } from '@d-orca/shared/permissions';
import {
  ActivityIcon,
  AdminIcon,
  AiAgentIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CadIcon,
  CalendarIcon,
  ChartColumnIcon,
  ChartPieIcon,
  CompassIcon,
  CpuIcon,
  DollarSignIcon,
  EmptyShieldIcon,
  FileStackIcon,
  GaugeIcon,
  GitCompareIcon,
  HardHatIcon,
  HelpCircleIcon,
  LogisticsIcon,
  MailIcon,
  MessageSquareIcon,
  MonitorIcon,
  PortfolioIcon,
  SparklesIcon,
  VideoIcon,
  WorkflowIcon,
} from '@utils/icons';

export type NavCtx = { lastSiteId?: string };

export type NavLeafNode = {
  kind: 'link';
  id: string;
  labelKey: string;
  icon: ReactNode;
  to: string | ((ctx: NavCtx) => string);
  /** Extra path patterns that should still mark this leaf (and its group)
   *  as active, for pages that redirect to a deeper URL than `to`. Only
   *  needed when the landing route pushes segments beyond `to`. */
  matchTo?: (string | ((ctx: NavCtx) => string))[];
  /** Optional feature gate. If set, the leaf is hidden from users who are
   *  not granted this feature. */
  feature?: Feature;
  /** Marks the page as still served from fixtures — renders a "MOCK"
   *  pill next to the nav label. Drop the flag once the surface is
   *  wired to a real backend. */
  mock?: boolean;
  /** Shown faded and not clickable — a placeholder for an unbuilt surface. */
  disabled?: boolean;
};

export type NavGroupNode = {
  kind: 'group';
  id: string;
  labelKey: string;
  icon: ReactNode;
  children: NavLeafNode[];
};

const ICON_SIZE = 20;

const withSite = (build: (siteId: string) => string) => (ctx: NavCtx) =>
  ctx.lastSiteId
    ? build(encodeURIComponent(ctx.lastSiteId))
    : build('').replace(/\/+/g, '/').replace(/\/$/, '');

export const NAV: NavGroupNode[] = [
  {
    kind: 'group',
    id: 'overview',
    labelKey: 'nav.section.overview',
    icon: <PortfolioIcon size={ICON_SIZE} />,
    children: [
      {
        kind: 'link',
        id: 'dashboard',
        labelKey: 'nav.item.dashboard.title',
        icon: <PortfolioIcon size={ICON_SIZE} />,
        to: withSite((s) => `/dashboard/${s}`),
      },
    ],
  },
  {
    kind: 'group',
    id: 'operations',
    labelKey: 'nav.section.operations',
    icon: <ActivityIcon size={ICON_SIZE} />,
    children: [
      {
        kind: 'link',
        id: 'monitor',
        labelKey: 'nav.item.monitor.title',
        icon: <MonitorIcon size={ICON_SIZE} />,
        to: withSite((s) => `/monitor/${s}`),
      },
      {
        kind: 'link',
        id: 'activitiesIssues',
        labelKey: 'nav.item.activitiesIssues.title',
        icon: <CalendarIcon size={ICON_SIZE} />,
        to: withSite((s) => `/activities/${s}`),
      },
      {
        kind: 'link',
        id: 'commsHub',
        labelKey: 'nav.item.commsHub.title',
        icon: <MessageSquareIcon size={ICON_SIZE} />,
        to: withSite((s) => `/comms/${s}`),
      },
      {
        kind: 'link',
        id: 'cameras',
        labelKey: 'nav.item.cameras.title',
        icon: <VideoIcon size={ICON_SIZE} />,
        to: withSite((s) => `/cameras/${s}`),
        mock: true,
      },
    ],
  },
  {
    kind: 'group',
    id: 'planning',
    labelKey: 'nav.section.planning',
    icon: <CalendarIcon size={ICON_SIZE} />,
    children: [
      {
        kind: 'link',
        id: 'scheduler',
        labelKey: 'nav.item.scheduler.title',
        icon: <ChartColumnIcon size={ICON_SIZE} />,
        to: withSite((s) => `/scheduler/${s}`),
        // The scheduler redirects to `/scheduler/:siteId/:scheduleNumber`
        // once a schedule loads; keep the group active there too.
        matchTo: [withSite((s) => `/scheduler/${s}/:scheduleNumber`)],
      },
      {
        kind: 'link',
        id: 'logistics',
        labelKey: 'nav.item.logistics.title',
        icon: <LogisticsIcon size={ICON_SIZE} />,
        to: withSite((s) => `/logistics/${s}`),
      },
      {
        kind: 'link',
        id: 'equipment',
        labelKey: 'nav.item.equipment.title',
        icon: <CpuIcon size={ICON_SIZE} />,
        to: withSite((s) => `/equipment/${s}`),
      },
    ],
  },
  {
    kind: 'group',
    id: 'engineering',
    labelKey: 'nav.section.engineering',
    icon: <CompassIcon size={ICON_SIZE} />,
    children: [
      {
        kind: 'link',
        id: 'cad',
        labelKey: 'nav.item.cad.title',
        icon: <CadIcon size={ICON_SIZE} />,
        to: withSite((s) => `/cad/${s}`),
        mock: true,
      },
      {
        kind: 'link',
        id: 'documents',
        labelKey: 'nav.item.documents.title',
        icon: <FileStackIcon size={ICON_SIZE} />,
        to: withSite((s) => `/documents/${s}`),
      },
    ],
  },
  {
    kind: 'group',
    id: 'performance',
    labelKey: 'nav.section.performance',
    icon: <GaugeIcon size={ICON_SIZE} />,
    children: [
      {
        kind: 'link',
        id: 'analytics',
        labelKey: 'nav.item.analytics.title',
        icon: <ChartPieIcon size={ICON_SIZE} />,
        to: withSite((s) => `/analytics/${s}`),
        feature: 'analytics.view',
      },
      {
        kind: 'link',
        id: 'productivity',
        labelKey: 'nav.item.productivity.title',
        icon: <GaugeIcon size={ICON_SIZE} />,
        to: withSite((s) => `/productivity/${s}`),
      },
      {
        kind: 'link',
        id: 'subcontractors',
        labelKey: 'nav.item.subcontractors.title',
        icon: <HardHatIcon size={ICON_SIZE} />,
        to: withSite((s) => `/subcontractors/${s}`),
        mock: true,
      },
    ],
  },
  {
    kind: 'group',
    id: 'platform',
    labelKey: 'nav.section.platform',
    icon: <SparklesIcon size={ICON_SIZE} />,
    children: [
      {
        kind: 'link',
        id: 'flowView',
        labelKey: 'nav.item.flowView.title',
        icon: <WorkflowIcon size={ICON_SIZE} />,
        to: '/flow-view',
        mock: true,
      },
      {
        kind: 'link',
        id: 'aiAgent',
        labelKey: 'nav.item.aiAgent.title',
        icon: <AiAgentIcon size={ICON_SIZE} />,
        to: withSite((s) => `/ai-agent/${s}`),
      },
    ],
  },
  {
    kind: 'group',
    id: 'business',
    labelKey: 'nav.section.business',
    icon: <BriefcaseIcon size={ICON_SIZE} />,
    children: [
      {
        kind: 'link',
        id: 'finance',
        labelKey: 'nav.item.finance.title',
        icon: <DollarSignIcon size={ICON_SIZE} />,
        to: '/finance',
        mock: true,
      },
    ],
  },
  {
    kind: 'group',
    id: 'admin',
    labelKey: 'nav.section.admin',
    icon: <EmptyShieldIcon size={ICON_SIZE} />,
    children: [
      {
        kind: 'link',
        id: 'admin',
        labelKey: 'nav.item.admin.title',
        to: withSite((s) => `/admin/${s}`),
        icon: <AdminIcon size={ICON_SIZE} />,
        feature: 'admin',
      },
      {
        kind: 'link',
        id: 'projectMerge',
        labelKey: 'nav.item.projectMerge.title',
        to: '/admin/project-merge',
        icon: <GitCompareIcon size={ICON_SIZE} />,
        feature: 'admin',
      },
    ],
  },
];

/** The Need help? group: rendered apart, at the bottom of the sidebar. */
export const HELP_NAV: NavGroupNode = {
  kind: 'group',
  id: 'help',
  labelKey: 'nav.section.help',
  icon: <HelpCircleIcon size={ICON_SIZE + 4} />,
  children: [
    {
      kind: 'link',
      id: 'pageGuides',
      labelKey: 'nav.item.pageGuides.title',
      icon: <BookOpenIcon size={ICON_SIZE} />,
      to: '/help',
      matchTo: ['/help/:slug'],
    },
    {
      kind: 'link',
      id: 'customerSupport',
      labelKey: 'nav.item.customerSupport.title',
      icon: <MailIcon size={ICON_SIZE} />,
      to: '/help',
      disabled: true,
    },
  ],
};

export function resolveTo(to: NavLeafNode['to'], ctx: NavCtx): string {
  return typeof to === 'function' ? to(ctx) : to;
}
